import invariant from 'invariant';
import React from 'react';
import styled, { css } from 'styled-components';
import { SpaceProps, space } from 'styled-system';

import { ListingLocationType } from 'api/types/shared.types';
import Box from 'components/Box';
import { colors, mediaRetina } from 'theme';
import { LISTING_LOCATION_TYPE, STATUSES } from 'utils/constants';
import {
  APPLICATION_STATUSES_ALL,
  getColor as getStatusColor,
} from 'utils/constants/ats';

const ALL_STATUSES = {
  ...STATUSES,
  ...APPLICATION_STATUSES_ALL,
  INACTIVE: getText('Inactive'),
  ACTIVE: getText('Active'),
};

const getStatus = (status) => ALL_STATUSES[status];

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition, @typescript-eslint/no-explicit-any
export const Tag = (props: Record<string, any>) => (
  <Box
    data-qa-id="listing-status"
    display="inline-block"
    py="2px"
    px="5px"
    borderRadius="4px"
    letterSpacing="1px"
    textTransform="uppercase"
    lineHeight={1}
    fontSize={12}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
);

const getLocationStyles = (type: ListingLocationType) => {
  switch (type) {
    case 'REMOTE':
      return {
        bg: colors.fuchsia,
        color: 'white',
      };

    case 'REMOTE_TEMPORARY_COVID':
      return {
        bg: colors.marigold,
        color: colors.charcoalGrey,
      };

    case 'ONSITE':
    default:
      return {
        bg: colors.turtleGreen,
        color: 'white',
      };
  }
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const LocationLabel = ({
  type,
  ...props
}: SpaceProps & {
  type: ListingLocationType;
}) => (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Tag {...getLocationStyles(type)} {...props}>
    {LISTING_LOCATION_TYPE[type]}
  </Tag>
);
const StyledStatus = styled.div<{ indicatorStyle?: string }>`
  position: relative;
  font-size: 14px;
  letter-spacing: 1.8px;
  color: ${colors.battleshipGrey};
  text-transform: uppercase;
  line-height: 1.35;
  padding-left: 21px;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 4px;
    border-radius: 50%;
    ${(props) =>
      props.indicatorStyle === 'outline'
        ? css`
            width: 8px;
            height: 8px;
            border: solid 1px ${props.color};
            ${mediaRetina} {
              border-width: 1.5px;
            }
          `
        : css`
            width: 11px;
            height: 11px;
            background-color: ${props.color};
          `};
  }
  ${space};
`;

// return the first application status
const getApplicationStatus = (status: ReadonlyArray<string> | string) => {
  // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | readonly string[]' is not assignabl... Remove this comment to see the full error message
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-param-reassign
  if (!Array.isArray(status)) status = [status];
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'find' does not exist on type 'string | r... Remove this comment to see the full error message
  return status.find((s) => APPLICATION_STATUSES_ALL[s]);
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const ListingStatusLabel = ({
  listingStatus,
  statusNote,
  asTag,
  forceSolid,
  ...props
}: SpaceProps & {
  listingStatus: ReadonlyArray<string> | string;
  statusNote?: string | null | undefined;
  asTag?: boolean;
  forceSolid?: boolean;
}) => {
  let status;
  let color = colors.scarlet;
  let textColor = 'white';
  let indicatorStyle = forceSolid ? 'outline' : 'solid';

  if (listingStatus.includes('PUBLISHED')) {
    status = 'PUBLISHED';
    color = colors.turtleGreen;
    indicatorStyle = 'solid';
  } else if (listingStatus.includes('REJECTED')) {
    status = 'REJECTED';
    color = colors.marigold;
    textColor = colors.charcoalGrey;
  } else if (listingStatus.includes('DRAFT')) {
    status = 'DRAFT';
    color = colors.marigold;
    textColor = colors.charcoalGrey;
  } else if (listingStatus.includes('PENDING')) {
    status = 'PENDING';
    color = colors.marigold;
    textColor = colors.charcoalGrey;
  } else if (listingStatus.includes('INVOICE')) {
    status = 'INVOICE';
    color = colors.turtleGreen;
  } else if (listingStatus.includes('ARCHIVED')) {
    status = 'ARCHIVED';
    color = colors.marigold;
    textColor = colors.charcoalGrey;
  } else if (listingStatus.includes('FROZEN')) {
    status = 'FROZEN';
  } else if (listingStatus.includes('HIDDEN')) {
    status = 'HIDDEN';
    color = colors.marigold;
    textColor = colors.charcoalGrey;
  } else if (listingStatus.includes('PULLED')) {
    status = 'PULLED';
  } else if (listingStatus.includes('DELETED')) {
    status = 'DELETED';
  } else if (listingStatus.includes('CHECKOUT')) {
    status = 'CHECKOUT';
    color = colors.marigold;
    textColor = colors.charcoalGrey;
  } else if (listingStatus.includes('SPAM')) {
    status = 'SPAM';
  } else if (listingStatus.includes('APPROVED')) {
    status = 'APPROVED';
    color = colors.turtleGreen;
    indicatorStyle = 'solid';
  } else if (getApplicationStatus(listingStatus)) {
    const statusColor = getStatusColor(
      getApplicationStatus(listingStatus) || 'APPLIED'
    );
    color = statusColor.bg;
    textColor = statusColor.color;
  } else if (listingStatus === 'ACTIVE') {
    color = colors.turtleGreen;
  } else if (listingStatus === 'INACTIVE') {
    color = colors.silver;
    textColor = colors.charcoalGrey;
  }

  if (
    typeof listingStatus === 'string' &&
    Object.keys(ALL_STATUSES).includes(listingStatus)
  ) {
    status = listingStatus;
  }

  invariant(status, 'Invalid status');
  return asTag ? (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Tag bg={color} color={textColor} {...props}>
      {getStatus(status)}
      {statusNote && ` (${statusNote})`}
    </Tag>
  ) : (
    <StyledStatus
      data-qa-id="listing-status"
      color={color}
      indicatorStyle={indicatorStyle}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {getStatus(status)}
      {statusNote && ` (${statusNote})`}
    </StyledStatus>
  );
};

export default ListingStatusLabel;
